/**
 * A CASCA DO APLICATIVO.
 *
 * O aplicativo do aluno **não tem navegação**: tem um estado, e o estado avança. Quem
 * responde vê a entrada, a questão, o fecho e a revisão — nada mais. Onde antes havia
 * uma barra com cinco abas para três públicos, agora há uma faixa de 52 px com a fita
 * de progresso e um botão `⋯`; num aparelho de 400 px isso devolve cerca de 150 px, que
 * é a diferença entre ver a figura e descobrir que existe uma figura.
 *
 * As ferramentas de adulto não sumiram: mudaram de porta. No rodapé da entrada, em
 * texto e sem ícone, há um **Sou professor** que abre a SALA (`#sala`) — um modo com
 * cabeçalho próprio, abas próprias e um "Sair da sala" permanente:
 *
 *  - **Turma** — o relatório: pauta de erros, calibração, quem respondeu o quê.
 *  - **Montar percurso** — quais itens entram, em que ordem, com que variante.
 *  - **Itens** — o laboratório de autoria.
 *  - **Mapa** — o banco como grafo, por dependência, assunto ou dificuldade.
 *
 * O aluno nunca lê a palavra "laboratório". O endereço continua guardando onde se está
 * (`#percurso`, `#sala`, `#montar`, `#laboratorio`, `#mapa`), então um link abre direto
 * no lugar certo, e as vistas não se destroem ao trocar: sair no meio e voltar retoma a
 * questão onde ela estava.
 */

import 'katex/dist/katex.min.css';
import { conta } from '../core/pt.ts';
import '../ui/tokens.css';
import '../ui/base.css';
import '../ui/pad.css';
import '../ui/interacoes.css';
import './app.css';

import { TelemetryCollector } from '../schema/telemetry.ts';
import { applyStoredTheme, cycleTheme, getTheme, THEME_LABEL } from '../ui/theme.ts';
import { criarVistaLaboratorio, type VistaLaboratorio } from './laboratorio.ts';
import { criarVistaMapa } from './mapa.ts';
import { criarVistaMontar } from './montar.ts';
import { criarVistaPercurso } from './percurso.ts';
import { criarVistaRelatorio } from './relatorio.ts';
import type { Aba, ContextoApp, Vista } from './tipos.ts';
import { configPadrao, percursoDoCodigo, salvarConfig } from './config.ts';
import { DEMO } from './demo.ts';
import { registrarServiceWorker } from './instalacao.ts';
import { idDaPessoa } from './pessoa.ts';

applyStoredTheme();

const raiz = document.getElementById('app');
if (!raiz) throw new Error('elemento #app não encontrado');
raiz.className = 'app';
const palcoRaiz: HTMLElement = raiz;

const telemetria = new TelemetryCollector({
  sessionId: `app-${Date.now().toString(36)}`,
  personId: idDaPessoa(),
});

/** As abas da sala, na ordem em que o professor as encontra. */
const SALA: readonly { readonly id: Aba; readonly rotulo: string }[] = [
  { id: 'relatorio', rotulo: 'Turma' },
  { id: 'montar', rotulo: 'Montar percurso' },
  { id: 'laboratorio', rotulo: 'Itens' },
  { id: 'mapa', rotulo: 'Mapa' },
];

const ENDERECOS: readonly Aba[] = ['percurso', 'montar', 'laboratorio', 'mapa', 'relatorio'];

const naSala = (aba: Aba): boolean => aba !== 'percurso';

// ------------------------------------------------------- pular para o conteúdo --

const pular = document.createElement('a');
pular.className = 'app-pular';
pular.href = '#conteudo';
pular.textContent = 'Pular para o conteúdo';

// --------------------------------------------------------- a faixa do percurso --

const topo = document.createElement('header');
topo.className = 'app-topo';

const marca = document.createElement('div');
marca.className = 'app-marca';
const nomeMarca = document.createElement('span');
nomeMarca.className = 'app-marca-nome';
nomeMarca.textContent = 'Itens iterativos';
marca.append(logotipo(), nomeMarca);

/**
 * O espaço da fita de progresso. Quando o percurso está numa questão, `percurso.ts`
 * põe aqui a fita — e é essa presença que o CSS usa (`:has(.app-progresso)`) para
 * recolher a marca: na tela da questão, a faixa é só progresso.
 */
const extra = document.createElement('div');
extra.className = 'app-topo-extra';

/** O `⋯`: o que se escolhe uma vez e não precisa ficar à vista enquanto se responde. */
const mais = document.createElement('div');
mais.className = 'app-mais';
const btnMais = document.createElement('button');
btnMais.type = 'button';
btnMais.className = 'app-mais-botao';
btnMais.setAttribute('aria-haspopup', 'true');
btnMais.setAttribute('aria-expanded', 'false');
btnMais.setAttribute('aria-label', 'Mais opções');
btnMais.textContent = '⋯';
const menu = document.createElement('div');
menu.className = 'app-mais-menu';
menu.hidden = true;

const itemTema = document.createElement('button');
itemTema.type = 'button';
itemTema.className = 'app-mais-item';
itemTema.textContent = THEME_LABEL[getTheme()];
itemTema.addEventListener('click', trocarTema);

const itemSala = document.createElement('button');
itemSala.type = 'button';
itemSala.className = 'app-mais-item';
itemSala.textContent = 'Sou professor';
itemSala.addEventListener('click', () => {
  fecharMenu();
  abrir('relatorio');
});

menu.append(itemTema, itemSala);
mais.append(btnMais, menu);

function fecharMenu(): void {
  menu.hidden = true;
  btnMais.setAttribute('aria-expanded', 'false');
}
btnMais.addEventListener('click', () => {
  menu.hidden = !menu.hidden;
  btnMais.setAttribute('aria-expanded', String(!menu.hidden));
});
document.addEventListener('click', (e) => {
  if (!menu.hidden && !mais.contains(e.target as Node)) fecharMenu();
});
document.addEventListener('keydown', (e) => {
  if (e.key === 'Escape') fecharMenu();
});

topo.append(marca, extra, mais);

// ------------------------------------------------------------------ a sala --

const topoSala = document.createElement('header');
topoSala.className = 'sala-topo';

const sair = document.createElement('button');
sair.type = 'button';
sair.className = 'sala-sair';
sair.textContent = '← Sair da sala';
sair.addEventListener('click', () => abrir('percurso'));

const abasSala = document.createElement('nav');
abasSala.className = 'sala-abas';
abasSala.setAttribute('role', 'tablist');
abasSala.setAttribute('aria-label', 'Sala do professor');
const botoesAba = new Map<Aba, HTMLButtonElement>();
for (const a of SALA) {
  const b = document.createElement('button');
  b.type = 'button';
  b.className = 'sala-aba';
  b.id = `aba-${a.id}`;
  b.setAttribute('role', 'tab');
  b.setAttribute('aria-controls', `painel-${a.id}`);
  b.tabIndex = -1;
  b.textContent = a.rotulo;
  b.addEventListener('click', () => abrir(a.id));
  botoesAba.set(a.id, b);
  abasSala.appendChild(b);
}

/**
 * Seta para o lado anda de aba em aba sem sair do teclado — é o que a ARIA espera de um
 * `tablist`, e é como quem usa leitor de tela descobre que há mais de uma.
 */
abasSala.addEventListener('keydown', (e) => {
  const passo = e.key === 'ArrowRight' ? 1 : e.key === 'ArrowLeft' ? -1 : 0;
  if (passo === 0) return;
  e.preventDefault();
  const i = SALA.findIndex((a) => a.id === abaAtual);
  const proxima = SALA[(i + passo + SALA.length) % SALA.length];
  if (proxima) {
    abrir(proxima.id);
    botoesAba.get(proxima.id)?.focus();
  }
});

const temaSala = document.createElement('button');
temaSala.type = 'button';
temaSala.className = 'app-tema';
temaSala.textContent = THEME_LABEL[getTheme()];
temaSala.addEventListener('click', trocarTema);

/**
 * Os dois botões de tema — o do `⋯` e o da sala — dizem sempre o mesmo. Antes só o da
 * sala atualizava o outro: trocado pelo `⋯`, o tema chegava à sala com o rótulo velho.
 */
function trocarTema(): void {
  const rotulo = THEME_LABEL[cycleTheme()];
  itemTema.textContent = rotulo;
  temaSala.textContent = rotulo;
}

topoSala.append(sair, abasSala, temaSala);

// -------------------------------------------------------------------- vistas --

const vistasEl = document.createElement('main');
vistasEl.className = 'app-vistas';
vistasEl.id = 'conteudo';
vistasEl.tabIndex = -1;

const aviso = document.createElement('div');
aviso.className = 'app-aviso';
aviso.setAttribute('role', 'status');
aviso.hidden = true;

raiz.append(pular, topo, topoSala, vistasEl, aviso);

let temporizadorAviso = 0;

const ctx: ContextoApp = {
  telemetria,
  topoExtra: extra,
  irPara: (aba) => abrir(aba),
  abrirNoLaboratorio(templateId, seed) {
    lab().abrirItem(templateId, seed);
    abrir('laboratorio');
  },
  avisar(texto) {
    aviso.textContent = texto;
    aviso.hidden = false;
    window.clearTimeout(temporizadorAviso);
    temporizadorAviso = window.setTimeout(() => {
      aviso.hidden = true;
    }, 3800);
  },
};

// O app como aplicativo instalável, que abre sem internet (ver `instalacao.ts`).
registrarServiceWorker((texto) => ctx.avisar(texto));

const vistas: Partial<Record<Aba, Vista>> = {};
let laboratorio: VistaLaboratorio | null = null;

function registrar(aba: Aba, v: Vista): void {
  vistas[aba] = v;
  v.element.hidden = true;
  if (naSala(aba)) {
    v.element.id = `painel-${aba}`;
    v.element.setAttribute('role', 'tabpanel');
    v.element.setAttribute('aria-labelledby', `aba-${aba}`);
  }
  vistasEl.appendChild(v.element);
}

function lab(): VistaLaboratorio {
  if (!laboratorio) {
    laboratorio = criarVistaLaboratorio(ctx);
    registrar('laboratorio', laboratorio);
  }
  return laboratorio;
}

/** As vistas nascem na primeira visita — o laboratório não gera nada até ser aberto. */
function vista(aba: Aba): Vista {
  if (aba === 'laboratorio') return lab();
  let v = vistas[aba];
  if (!v) {
    v =
      aba === 'percurso'
        ? criarVistaPercurso(ctx)
        : aba === 'montar'
          ? criarVistaMontar(ctx)
          : aba === 'relatorio'
            ? criarVistaRelatorio(ctx)
            : criarVistaMapa(ctx);
    registrar(aba, v);
  }
  return v;
}

let abaAtual: Aba | null = null;

function abrir(aba: Aba): void {
  if (abaAtual === aba) return;
  if (abaAtual) {
    const anterior = vistas[abaAtual];
    if (anterior) {
      anterior.element.hidden = true;
      anterior.esconder?.();
    }
  }
  abaAtual = aba;
  const v = vista(aba);
  v.element.hidden = false;
  v.mostrar?.();

  // Duas cascas, uma de cada vez: a faixa do percurso e o cabeçalho da sala.
  const sala = naSala(aba);
  palcoRaiz.classList.toggle('app-em-sala', sala);
  topo.hidden = sala;
  topoSala.hidden = !sala;
  fecharMenu();
  for (const [id, b] of botoesAba) {
    const marcada = id === aba;
    b.setAttribute('aria-selected', String(marcada));
    b.tabIndex = marcada ? 0 : -1;
  }

  // O endereço guarda onde se está — mas, se o ambiente não permitir mexer no histórico
  // (página embutida, sandbox), a vista abre do mesmo jeito.
  try {
    if (location.hash !== `#${aba}`) history.replaceState(null, '', `#${aba}`);
  } catch {
    // sem histórico: segue sem guardar a vista no endereço
  }
  window.scrollTo({ top: 0, behavior: 'instant' as ScrollBehavior });
}

function abaDoEndereco(): Aba | null {
  const h = location.hash.replace('#', '');
  // `#sala` é a porta do professor: entra pela Turma, que é o que ele quer ver.
  if (h === 'sala') return 'relatorio';
  return ENDERECOS.some((a) => a === h) ? (h as Aba) : null;
}

/**
 * Um link de percurso (`#p=P1.…`, ver `codigoDoPercurso`): carrega o percurso recebido
 * no lugar do atual e avisa quantas questões chegaram.
 */
function percursoDoEndereco(): boolean {
  if (!location.hash.startsWith('#p=')) return false;
  const c = percursoDoCodigo(location.hash.slice(3));
  if (!c) {
    ctx.avisar('O link não traz um percurso válido.');
    return false;
  }
  salvarConfig(c);
  ctx.avisar(`Percurso recebido pelo link: ${conta(c.entradas.length, 'questao')}.`);
  try {
    history.replaceState(null, '', '#percurso');
  } catch {
    // sem histórico: o código fica no endereço, e nada quebra
  }
  return true;
}

/**
 * `#demo`: carrega o percurso de demonstração (doze questões de variante fixa, ver
 * `demo.ts`) e abre o percurso. É o endereço que se digita antes de apresentar.
 */
function demoDoEndereco(): boolean {
  if (location.hash !== '#demo') return false;
  salvarConfig(configPadrao());
  ctx.avisar(`Percurso de demonstração carregado: ${conta(DEMO.length, 'questao')}.`);
  try {
    history.replaceState(null, '', '#percurso');
  } catch {
    // sem histórico: o endereço fica como está, e nada quebra
  }
  return true;
}

window.addEventListener('hashchange', () => {
  if (demoDoEndereco()) {
    abrir('percurso');
    return;
  }
  if (percursoDoEndereco()) {
    abrir('percurso');
    return;
  }
  const a = abaDoEndereco();
  if (a) abrir(a);
});

abrir(demoDoEndereco() || percursoDoEndereco() ? 'percurso' : (abaDoEndereco() ?? 'percurso'));

// ---------------------------------------------------------------------- marca --

/** Marca do app: um compasso — duas pernas, a charneira e o arco que elas deixam. */
function logotipo(): SVGElement {
  const ns = 'http://www.w3.org/2000/svg';
  const svg = document.createElementNS(ns, 'svg');
  svg.setAttribute('viewBox', '0 0 24 24');
  svg.setAttribute('aria-hidden', 'true');
  const g = document.createElementNS(ns, 'g');
  g.setAttribute('fill', 'none');
  g.setAttribute('stroke', 'currentColor');
  g.setAttribute('stroke-width', '1.9');
  g.setAttribute('stroke-linecap', 'round');
  g.setAttribute('stroke-linejoin', 'round');
  for (const d of [
    'M12 3.6 L6.6 18.4',
    'M12 3.6 L17.4 18.4',
    'M5.4 17.4 A 8.6 8.6 0 0 0 18.6 17.4',
  ]) {
    const p = document.createElementNS(ns, 'path');
    p.setAttribute('d', d);
    g.appendChild(p);
  }
  const c = document.createElementNS(ns, 'circle');
  c.setAttribute('cx', '12');
  c.setAttribute('cy', '3.6');
  c.setAttribute('r', '1.7');
  c.setAttribute('fill', 'currentColor');
  c.setAttribute('stroke', 'none');
  svg.append(g, c);
  return svg;
}
